import { useQuery } from '@apollo/client/react'
import {
  Badge,
  ErrorState,
  LoadingState,
  Notice,
  PageLayout,
  Section,
  buttonClasses,
  useLanguage,
  useTranslate,
  PANEL_SURFACE,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { usePageTitle } from '../app/page-title.js'
import { DowngradeSelection } from '../features/subscription/DowngradeSelection.js'
import { PlanActions } from '../features/subscription/PlanActions.js'
import { planName } from '../features/subscription/planName.js'
import { SuspendedResources } from '../features/subscription/SuspendedResources.js'
import {
  SubscriptionResourceList,
  SubscriptionResourceRow,
} from '../features/subscription/SubscriptionResourceList.js'
import { UsageSummary } from '../features/subscription/UsageSummary.js'
import { MySubscriptionDocument } from '../graphql/generated/operations.js'
import { useFormat } from '../lib/format.js'

/**
 * Plan and usage: what this account is allowed, what it is using, and what the
 * tutor can do about it.
 *
 * Read top to bottom the screen answers four questions in the order a tutor
 * asks them. What plan am I on and what does it cost. Is anything wrong right
 * now. What am I using. What are my options.
 *
 * ── Nothing on this screen decides anything ──────────────────────────────
 *
 * Every number comes from one `mySubscription` snapshot, and the list of
 * available actions comes with it. The screen does not compute "remaining",
 * does not work out whether an upgrade request is allowed, and does not decide
 * what a downgrade would suspend — all four are the server's, resolved
 * together so they are one moment rather than four.
 *
 * ── And nothing on it takes money ────────────────────────────────────────
 *
 * V1 has no payment processor. The prices are plan metadata; the upgrade
 * control sends a *request* that an administrator answers. There is no
 * checkout here, no card form, and no copy anywhere that says "subscribed" or
 * "payment successful" — see `PlanActions`.
 */
export function SubscriptionScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()
  usePageTitle(t('subscription.title'))

  const { data, loading, error } = useQuery(MySubscriptionDocument)

  /**
   * The full-screen states are for the **first** load only.
   *
   * Every action on this screen refetches it, and Apollo reports `loading`
   * again while a refetch is in flight. Returning the loader on that would
   * unmount the whole screen mid-action — which throws away the in-place
   * confirmation the tutor is waiting to read, and makes a plan change flash
   * "Loading…" over work they just did. Once there is data, the screen keeps
   * rendering it and the controls show their own loading state.
   */
  if (loading && !data) {
    return (
      <PageLayout width="operational">
        <LoadingState className="p-0 py-8">{t('common.loading')}</LoadingState>
      </PageLayout>
    )
  }
  if (error && !data) {
    return (
      <PageLayout width="operational">
        <ErrorState className="p-0 py-8">{t('subscription.loadError')}</ErrorState>
      </PageLayout>
    )
  }
  if (!data) return null

  const subscription = data.mySubscription

  const price =
    subscription.price.amountMinorUnits === 0
      ? t('subscription.free')
      : t('subscription.perMonth', [format.money(subscription.price)])

  return (
    // In the order of the tutor's questions: which plan, how much of it, what
    // is changing, and only then the other plans. The first two are bounded
    // sibling regions because together they are the account overview; usage
    // metrics stay flat inside that one boundary. Later sections remain on
    // the canvas rather than inheriting overview chrome.
    <PageLayout width="operational">
      <div className="grid grid-cols-1 gap-6 desktop:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] desktop:items-stretch">
        <Section id="current-plan" title={t('subscription.currentPlan')} className={PANEL_SURFACE}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-h2 font-bold text-text">
              {planName(t, subscription.planCode)}
            </span>
            <span className="text-body text-text-secondary">{price}</span>
          </div>
          <p className="text-body-sm text-text-secondary">
            {t('subscription.since', [format.date(subscription.since)])}
            {subscription.periodEndsAt
              ? ` · ${t('subscription.periodEnds', [format.date(subscription.periodEndsAt)])}`
              : ''}
            {subscription.accessEndsAt
              ? ` · ${t('subscription.accessEnds', [format.date(subscription.accessEndsAt)])}`
              : ''}
          </p>

          {/* A scheduled change is part of what plan this is: said here, beside
              the plan's name, as a fact. The notice below, with its way to
              cancel, is where it is acted on. */}
          {subscription.scheduledPlanCode ? (
            <p className="text-body text-text">
              {t('subscription.scheduledBody', [
                planName(t, subscription.scheduledPlanCode),
                format.date(subscription.scheduledEffectiveAt ?? ''),
              ])}
            </p>
          ) : null}

          {/* Paid access that ran out on the clock rather than by anybody's
              action. Worth an alert: the account is on Free and the tutor may
              not know why their capacity changed. */}
          {subscription.lapsed ? (
            <Notice tone="error" title={t('subscription.lapsedTitle')}>
              {t('subscription.lapsedBody')}
            </Notice>
          ) : null}

          {/* Over the limit, which on this product means "nothing was taken
              away, and you cannot add more". Both halves are said, because a
              tutor who reads only the first half will go looking for what they
              lost. */}
          {subscription.students.overBy > 0 || subscription.practices.overBy > 0 ? (
            <Notice tone="warning" title={t('subscription.overLimitTitle')}>
              {subscription.students.overBy > 0 ? (
                <p>
                  {t('subscription.overLimitStudents', [
                    subscription.students.used,
                    subscription.students.limit,
                  ])}
                </p>
              ) : null}
              {subscription.practices.overBy > 0 && subscription.practices.limit !== null ? (
                <p>
                  {t('subscription.overLimitPractices', [
                    subscription.practices.used,
                    subscription.practices.limit,
                  ])}
                </p>
              ) : null}
              <p className="mt-1">{t('subscription.overLimitBody')}</p>
            </Notice>
          ) : null}
        </Section>

        <Section id="usage" title={t('subscription.usageHeading')} className={PANEL_SURFACE}>
          <UsageSummary subscription={subscription} />
        </Section>
      </div>

      {/* Practices a student cannot open for a reason freeing a slot would not
          fix: too long for the plan, or timed without one. Neither has been
          changed, and the copy says so — truncating a tutor's questions or
          silently untiming an exam would both be editing their work. */}
      {subscription.blockedPractices.length > 0 ? (
        <Section id="blocked-practices" title={t('subscription.blockedHeading')}>
          <p className="text-body text-text-secondary">{t('subscription.blockedBody')}</p>
          <SubscriptionResourceList>
            {subscription.blockedPractices.map((practice) => (
              <SubscriptionResourceRow
                key={practice.practiceId}
                name={practice.title}
                reason={
                  <span className="flex flex-col gap-1">
                    {practice.overQuestionLimit ? (
                      <span>
                        {t('subscription.blockedTooLong', [
                          practice.questionCount,
                          subscription.questionLimit,
                        ])}
                      </span>
                    ) : null}
                    {practice.timedWithoutPlan ? (
                      <span>{t('subscription.blockedTimed')}</span>
                    ) : null}
                  </span>
                }
                state={
                  <Badge status="error" className="px-3 py-1.5">
                    {t('subscription.blockedState')}
                  </Badge>
                }
                // Both fixes live on the practice page: its settings turn the
                // time limit off, and a new version shortens it.
                action={
                  <Link
                    to="/$language/practices/$practiceId"
                    params={{ language, practiceId: practice.practiceId }}
                    className={buttonClasses({ variant: 'secondary', size: 'sm' })}
                  >
                    {t('subscription.blockedAction')}
                  </Link>
                }
              />
            ))}
          </SubscriptionResourceList>
        </Section>
      ) : null}

      <DowngradeSelection subscription={subscription} />

      <SuspendedResources subscription={subscription} />

      <PlanActions subscription={subscription} plans={data.subscriptionPlans} />
    </PageLayout>
  )
}
